import { useBusinessEntityLocations } from '@commenda/api';
import {
  Badge,
  Button,
  DataTable,
  Dropdown,
  FlagComponent,
  IconButton,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import {
  IBusinessEntity,
  ILocation,
  LocationTypeRevamp,
} from '@commenda/types';
import {
  copyToClipboard,
  getPrintableBusinessLocation,
  LOCATION_TYPE_TO_TEXT,
} from '@commenda/utils';
import { Country as CountryPkg } from 'country-state-city';
import { useMemo, useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { DataPoint } from '../../../common/DataPoint';
import { AddLocationModal } from './Modals/AddLocationModal';
import { AddRegisteredAddressModal } from './Modals/AddRegisteredAddressModal';
import { DeleteLocationModal } from './Modals/DeleteLocationModal';

interface HideIconConfig {
  registeredOfficeAddress?: boolean;
  mailingAddress?: boolean;
}

interface LocationsProps {
  businessEntity: IBusinessEntity;
  hideTitle?: boolean;
  hideEntityLocationsSection?: boolean;
  hideEditIconForFields?: HideIconConfig;
  hideCopyIconForFields?: HideIconConfig;
  disabled?: boolean;
}

const getLocationDisplayName = (locationType: LocationTypeRevamp): string => {
  const locationNames = {
    [LocationTypeRevamp.MAILING_ADDRESS]: 'Mailing address',
    [LocationTypeRevamp.REGISTERED_OFFICE]: 'Registered address',
  };

  return locationNames[locationType] || 'Office address';
};

const formatLocationDateForTable = (value?: Date | string | null) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatLocationForTable = (location: ILocation) => {
  return location.locationTypes.map((locationType) => ({
    id: location.id,
    address: location.address,
    locationTypes: [locationType],
    locationTypesArray: location.locationTypes,
    name: location.name || getLocationDisplayName(locationType),
    country: location.address.country,
    startDate: location.startDate,
    endDate: location.endDate,
    employeeCount: location.employeeCount,
    presenceType: locationType, // Add for filtering
    isVirtualAddress: location.isVirtualAddress
  }));
};

export const Locations = ({
  businessEntity,
  hideTitle = false,
  hideEntityLocationsSection = false,
  hideEditIconForFields = {},
  hideCopyIconForFields = {},
  disabled = false,
}: LocationsProps) => {
  const {
    data: businessEntityLocationsResponse,
    error: businessEntityLocationsError,
    mutate: mutateBusinessEntityLocations,
  } = useBusinessEntityLocations(businessEntity.id);

  const addAddressModalVisibility = useToggle();
  const addLocationModalVisibility = useToggle();
  const deleteLocationModalVisibility = useToggle();
  const editMailingAddressModalVisibility = useToggle();

  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(
    null
  );

  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | string[]>
  >({});

  const { setNotification } = useNotification();

  const locations = useMemo(
    () => businessEntityLocationsResponse?.locations || [],
    [businessEntityLocationsResponse]
  );

  const formattedLocations = useMemo(
    () => locations.flatMap(formatLocationForTable),
    [locations]
  );

  const allCountries = useMemo(() => {
    return Array.from(
      new Set(formattedLocations.map((loc) => loc.country))
    ).sort();
  }, [formattedLocations]);

  const filteredLocations = useMemo(() => {
    let filtered = formattedLocations;

    // Filter by country
    if (activeFilters.country && typeof activeFilters.country === 'string') {
      filtered = filtered.filter(
        (loc) => loc.country === activeFilters.country
      );
    }

    // Filter by presence type (multi-select)
    if (
      activeFilters.presenceType &&
      Array.isArray(activeFilters.presenceType) &&
      activeFilters.presenceType.length > 0
    ) {
      filtered = filtered.filter((loc) => {
        // Since each formatted location has a single presenceType, check if it's included
        return activeFilters.presenceType.includes(loc.presenceType);
      });
    }

    return filtered;
  }, [formattedLocations, activeFilters]);

  const registeredOfficeAddressLocation = useMemo(
    () =>
      locations.find((location) =>
        location.locationTypes
          .map((locationType) => locationType)
          .includes(LocationTypeRevamp.REGISTERED_OFFICE)
      ),
    [locations]
  );

  const mailingAddressLocation = useMemo(
    () =>
      locations.find((location) =>
        location.locationTypes
          .map((locationType) => locationType)
          .includes(LocationTypeRevamp.MAILING_ADDRESS)
      ),
    [locations]
  );

  const registeredOfficeAddress = registeredOfficeAddressLocation?.address;
  const mailingAddress = mailingAddressLocation?.address;

  const hasRegisteredAddresses = Boolean(registeredOfficeAddress);
  const hasMailingAddress = Boolean(mailingAddress);

  const printableRegisteredOfficeAddress = getPrintableBusinessLocation(
    registeredOfficeAddress
  );
  const printableMailingAddress = getPrintableBusinessLocation(mailingAddress);

  const businessEntityInfo = {
    id: businessEntity.id,
    name: businessEntity.generalCorporationInfo.legalName,
    country: businessEntity.generalCorporationInfo.incorporationCountry,
  };

  const handleCopyAddress = (address: string, addressType: string) => {
    copyToClipboard(address);
    setNotification({
      title: 'Copied to clipboard',
      message: `The ${addressType} has been copied to your clipboard.`,
      show: true,
      valid: true,
    });
  };

  const handleEditLocation = (location: any) => {
    const { locationTypes } = location;

    if (locationTypes.includes(LocationTypeRevamp.REGISTERED_OFFICE)) {
      editMailingAddressModalVisibility.off();
      setSelectedLocation(null);
      addAddressModalVisibility.show();
    } else if (locationTypes.includes(LocationTypeRevamp.MAILING_ADDRESS)) {
      editMailingAddressModalVisibility.on();
      setSelectedLocation(null);
      addAddressModalVisibility.show();
    } else {
      setSelectedLocation(location);
      addLocationModalVisibility.show();
    }
  };

  const handleDeleteLocation = (location: ILocation) => {
    setSelectedLocation(location);
    deleteLocationModalVisibility.show();
  };

  const createTableColumns = () => [
    {
      accessorFn: null,
      accessorKey: 'name',
      header: 'Location name',
      title: 'Location name',
      cell: ({ row }: any) => (
        <span className="txt-small-medium">{row.original.name}</span>
      ),
    },
    {
      accessorFn: null,
      accessorKey: 'locationTypes',
      header: 'Presence type',
      title: 'Presence type',
      cell: ({ row }: any) => (
        <div className="flex flex-col justify-start items-start gap-xs">
          {row.original.locationTypes.map(
            (locationType: LocationTypeRevamp) => (
              <Badge key={locationType} variant="gray" size="sm">
                {LOCATION_TYPE_TO_TEXT[locationType]}
              </Badge>
            )
          )}
        </div>
      ),
    },
    {
      accessorFn: null,
      accessorKey: 'country',
      header: 'Country',
      title: 'Country',
      cell: ({ row }: any) => {
        const countryName = CountryPkg.getCountryByCode(
          row.original.country
        ).name;
        return (
          <div className="flex items-center gap-xs">
            <FlagComponent
              country={row.original.country}
              countryName={countryName}
              _className="size-5"
            />
            <span>{countryName}</span>
          </div>
        );
      },
    },
    {
      accessorFn: null,
      accessorKey: 'startDate',
      header: 'Start date',
      title: 'Start date',
      cell: ({ row }: any) => (
        <span className="txt-small-medium">
          {formatLocationDateForTable(row.original.startDate)}
        </span>
      ),
    },
    {
      accessorFn: null,
      accessorKey: 'endDate',
      header: 'End date',
      title: 'End date',
      cell: ({ row }: any) => (
        <span className="txt-small-medium">
          {formatLocationDateForTable(row.original.endDate)}
        </span>
      ),
    },
    {
      accessorFn: null,
      accessorKey: 'address',
      header: 'Address',
      title: 'Address',
      cell: ({ row }: any) => (
        <span className="txt-small-medium block truncate max-w-[500px]">
          {getPrintableBusinessLocation(row.original.address)}
        </span>
      ),
    },
    {
      accessorFn: null,
      accessorKey: 'actions',
      header: '',
      title: '',
      cell: ({ row }: any) => (
        <div className="flex justify-end w-full">
          <Dropdown
            menu={{
              groups: [
                {
                  items: disabled
                    ? []
                    : [
                      {
                        label: 'Edit',
                        onClick: () => handleEditLocation(row.original),
                      },
                      {
                        label: 'Delete',
                        onClick: () => handleDeleteLocation(row.original),
                      },
                    ],
                },
              ],
            }}
            align="end"
          >
            {!disabled && (
              <IconButton
                icon={<Icons.DotsVertical />}
                size="lg"
                aria-label="More options"
              />
            )}
          </Dropdown>
        </div>
      ),
    },
  ];

  // Loading state
  if (!businessEntityLocationsResponse || businessEntityLocationsError) {
    if (businessEntityLocationsError) {
      console.error(businessEntityLocationsError);
    }

    return (
      <div className="flex flex-col gap-6xl">
        <div className="flex flex-col gap-8xl">
          <div className="flex flex-col gap-xs max-w-[720px]">
            <h3 className="txt-large-bold">Business Locations & Operations</h3>
            <p className="txt-small-regular text-text-tertiary">
              Track all the places where your business has a presence — offices,
              warehouses, retail stores, or even states and countries where
              employees work remotely. These locations can trigger local
              compliance obligations, such as taxes, registrations, or filings.
            </p>
          </div>
          <LoadingIndicator _className="mx-auto" size={64} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2xl">
      {/* Modals */}
      <AddRegisteredAddressModal
        isOpen={addAddressModalVisibility.visible}
        onClose={addAddressModalVisibility.off}
        registeredOfficeAddress={
          registeredOfficeAddressLocation as ILocation & {
            locationTypes: LocationTypeRevamp[];
          }
        }
        mailingAddress={
          mailingAddressLocation as ILocation & {
            locationTypes: LocationTypeRevamp[];
          }
        }
        businessEntity={businessEntityInfo}
        isMailingAddress={editMailingAddressModalVisibility.visible}
        isMailingAddressAdded={Boolean(mailingAddressLocation)}
        isRegisteredAddressAdded={Boolean(registeredOfficeAddressLocation)}
        onSuccess={mutateBusinessEntityLocations}
      />

      <AddLocationModal
        isOpen={addLocationModalVisibility.visible}
        onClose={addLocationModalVisibility.off}
        businessEntityId={businessEntity.id}
        onSuccess={mutateBusinessEntityLocations}
        selectedLocation={
          selectedLocation as ILocation & {
            locationTypesArray: LocationTypeRevamp[];
          }
        }
      />

      <DeleteLocationModal
        isOpen={deleteLocationModalVisibility.visible}
        onClose={deleteLocationModalVisibility.off}
        businessEntityId={businessEntity.id}
        onSuccess={mutateBusinessEntityLocations}
        selectedLocation={
          selectedLocation as ILocation & {
            locationTypesArray: LocationTypeRevamp[];
          }
        }
      />

      <div className="flex flex-col gap-6xl">
        {/* Entity Addresses Section */}
        <div className="flex flex-col gap-2xl">
          <div className="flex flex-col items-start gap-3xl">
            {!hideTitle && (
              <div className="flex flex-col gap-xs max-w-[720px]">
                <h3 className="txt-large-bold">
                  Business Locations and Operations
                </h3>
                <p className="txt-small-regular text-text-tertiary">
                  Track all the places where your business has a presence —
                  offices, warehouses, retail stores, or even states and
                  countries where employees work remotely. These locations can
                  trigger local compliance obligations, such as taxes,
                  registrations, or filings.
                </p>
              </div>
            )}
            <p className="text-base font-semibold text-text-primary">
              Entity Registered Address
            </p>
          </div>

          <div className="rounded-xl overflow-clip divide-y divide-border-primary">
            {/* Registered Address */}
            <DataPoint
              title="Registered Address"
              description="This is the legal name and jurisdiction where the entity is officially registered."
              value={
                hasRegisteredAddresses ? (
                  printableRegisteredOfficeAddress
                ) : !disabled ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      editMailingAddressModalVisibility.off();
                      addAddressModalVisibility.show();
                    }}
                  >
                    <Icons.Plus className="size-4" />
                    <span>Add</span>
                  </Button>
                ) : null
              }
              disabled={disabled}
              editConfig={
                hasRegisteredAddresses &&
                  !hideEditIconForFields.registeredOfficeAddress
                  ? {
                    isAllowed: true,
                    onEdit: () => {
                      editMailingAddressModalVisibility.off();
                      addAddressModalVisibility.show();
                    },
                  }
                  : undefined
              }
              onCopy={
                hasRegisteredAddresses &&
                  !hideCopyIconForFields.registeredOfficeAddress
                  ? () =>
                    handleCopyAddress(
                      printableRegisteredOfficeAddress,
                      'registered address'
                    )
                  : undefined
              }
            />
            {/* Mailing Address */}
            <DataPoint
              title="Mailing Address"
              description="The address where official documents and government notices are sent. This may differ from your registered or operational address."
              value={
                hasMailingAddress ? (
                  printableMailingAddress
                ) : !disabled ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      editMailingAddressModalVisibility.show();
                      addAddressModalVisibility.show();
                    }}
                  >
                    <Icons.Plus className="size-4" />
                    <span>Add</span>
                  </Button>
                ) : null
              }
              disabled={disabled}
              editConfig={
                hasMailingAddress && !hideEditIconForFields.mailingAddress
                  ? {
                    isAllowed: true,
                    onEdit: () => {
                      editMailingAddressModalVisibility.show();
                      addAddressModalVisibility.show();
                    },
                  }
                  : undefined
              }
              onCopy={
                hasMailingAddress && !hideCopyIconForFields.mailingAddress
                  ? () =>
                    handleCopyAddress(
                      printableMailingAddress,
                      'mailing address'
                    )
                  : undefined
              }
            />
          </div>
        </div>

        {/* Physical Presence Section */}
        {!hideEntityLocationsSection && (
          <div className="flex flex-col gap-2xl">
            <div className="flex items-start justify-between gap-3xl">
              {!hideTitle && (
                <div className="flex flex-col gap-xs max-w-[720px]">
                  <h3 className="txt-large-bold">Business Presence</h3>
                  <p className="txt-small-regular text-text-tertiary">
                    List all locations where your business has physical or
                    operational activity.
                  </p>
                </div>
              )}
            </div>

            {formattedLocations.length > 0 ? (
              <DataTable
                data={filteredLocations}
                rowDataAttributes={(row) => ({
                  'data-ai-item': 'Location',
                  ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
                })}
                columnGroups={[
                  {
                    id: 'locations-main',
                    columns: createTableColumns().slice(0, -1),
                  },
                  {
                    id: 'locations-actions',
                    sticky: 'right' as const,
                    columns: [
                      {
                        id: 'actions',
                        accessorKey: 'actions',
                        header: '',
                        title: '',
                        width: 64,
                        cell: ({ row }: any) => (
                          <div className="flex justify-end w-full">
                            <Dropdown
                              menu={{
                                groups: [
                                  {
                                    items: disabled
                                      ? []
                                      : [
                                        {
                                          label: 'Edit',
                                          onClick: () =>
                                            handleEditLocation(row.original),
                                        },
                                        {
                                          label: 'Delete',
                                          onClick: () =>
                                            handleDeleteLocation(
                                              row.original
                                            ),
                                        },
                                      ],
                                  },
                                ],
                              }}
                              align="end"
                            >
                              {!disabled && (
                                <IconButton
                                  icon={<Icons.DotsVertical />}
                                  size="lg"
                                  aria-label="More options"
                                />
                              )}
                            </Dropdown>
                          </div>
                        ),
                      },
                    ],
                  },
                ]}
                emptyStateConfig={{
                  title: 'No locations found',
                  description: 'Your locations will appear here.',
                }}
                searchConfig={{
                  isEnabled: true,
                  placeholder: 'Search locations',
                  columnsToEnableSearchFor: ['name', 'country', 'address'],
                }}
                paginationConfig={{
                  isEnabled: true,
                  pageSize: 10,
                  type: 'PAGE',
                }}
                addRowConfig={{
                  isEnabled: !disabled,
                  renderAddRow: () => (
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedLocation(null);
                        addLocationModalVisibility.show();
                      }}
                    >
                      <Icons.Plus className="size-4" />
                      <span>Add presence</span>
                    </Button>
                  ),
                }}
                filterConfig={{
                  filters: [
                    {
                      label: 'Country',
                      showSearch: true,
                      type: 'SINGLE_SELECT',
                      columnId: 'country',
                      icon: <Icons.Globe04 className="size-4" />,
                      options: allCountries.map((country) => ({
                        value: country,
                        label:
                          CountryPkg.getCountryByCode(country)?.name || country,
                      })),
                      placeholder: 'Select country',
                    },
                    {
                      label: 'Presence type',
                      showSearch: true,
                      type: 'MULTI_SELECT',
                      columnId: 'presenceType',
                      icon: <Icons.Grid01 className="size-4" />,
                      options: Object.values(LocationTypeRevamp)
                        .filter(
                          (type) =>
                            type !==
                            LocationTypeRevamp.REGISTERED_OFFICE &&
                            type !== LocationTypeRevamp.MAILING_ADDRESS
                        )
                        .map((type) => ({
                          value: type,
                          label: LOCATION_TYPE_TO_TEXT[type],
                        })),
                      placeholder: 'Select presence types',
                    },
                  ],
                  activeFilters,
                  onFilterChange: setActiveFilters,
                }}
                columnSelectorConfig={{
                  isEnabled: true,
                }}
              />
            ) : (
              <div className="flex flex-col gap-3xl justify-center items-center rounded-xl py-6 px-10 border border-border-primary bg-bg-secondary">
                <div className="flex flex-col gap-xs justify-center items-center">
                  <h3 className="txt-large-bold text-text-primary">
                    No business locations added yet
                  </h3>
                  <p className="txt-small-regular text-text-secondary">
                    {disabled
                      ? 'No entity locations have been added yet.'
                      : 'Add an entity location to track nexus and permanent establishment'}
                  </p>
                </div>
                {!disabled && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={addLocationModalVisibility.show}
                  >
                    <Icons.Plus className="size-4" />
                    <span>Add Physical Presence</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
